/**
 * Minimal, dependency-free QR Code generator (byte mode, versions 1–10,
 * ECC levels L/M/Q/H). Implements GF(256) Reed–Solomon, the standard module
 * placement, all 8 masks with penalty scoring, and BCH format info per
 * ISO/IEC 18004. Returns a square boolean matrix (true = dark module).
 */

export type ErrorCorrectionLevel = "L" | "M" | "Q" | "H";

// --- GF(256) arithmetic (primitive polynomial 0x11d) -----------------------
const EXP: number[] = new Array(512).fill(0);
const LOG: number[] = new Array(256).fill(0);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
})();
const exp = (i: number) => EXP[i]!;
const log = (i: number) => LOG[i]!;
const gfMul = (a: number, b: number) => (a === 0 || b === 0 ? 0 : exp(log(a) + log(b)));

function rsGenPoly(n: number): number[] {
  let poly = [1];
  for (let i = 0; i < n; i++) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j++) {
      next[j] = next[j]! ^ poly[j]!;
      next[j + 1] = next[j + 1]! ^ gfMul(poly[j]!, exp(i));
    }
    poly = next;
  }
  return poly;
}

function rsEncode(data: number[], ecLen: number): number[] {
  const gen = rsGenPoly(ecLen);
  const res = new Array<number>(ecLen).fill(0);
  for (const byte of data) {
    const factor = byte ^ res[0]!;
    res.shift();
    res.push(0);
    if (factor !== 0) for (let i = 0; i < ecLen; i++) res[i] = res[i]! ^ gfMul(gen[i + 1]!, factor);
  }
  return res;
}

// --- Capacity / EC block tables (versions 1–10) ----------------------------
// [ecCodewordsPerBlock, [[blockCount, dataCodewords], ...]]
type EcEntry = [number, [number, number][]];
const EC: Record<ErrorCorrectionLevel, EcEntry[]> = {
  L: [
    [7, [[1, 19]]],
    [10, [[1, 34]]],
    [15, [[1, 55]]],
    [20, [[1, 80]]],
    [26, [[1, 108]]],
    [18, [[2, 68]]],
    [20, [[2, 78]]],
    [24, [[2, 97]]],
    [30, [[2, 116]]],
    [
      18,
      [
        [2, 68],
        [2, 69],
      ],
    ],
  ],
  M: [
    [10, [[1, 16]]],
    [16, [[1, 28]]],
    [26, [[1, 44]]],
    [18, [[2, 32]]],
    [24, [[2, 43]]],
    [16, [[4, 27]]],
    [18, [[4, 31]]],
    [
      22,
      [
        [2, 38],
        [2, 39],
      ],
    ],
    [
      22,
      [
        [3, 36],
        [2, 37],
      ],
    ],
    [
      26,
      [
        [4, 43],
        [1, 44],
      ],
    ],
  ],
  Q: [
    [13, [[1, 13]]],
    [22, [[1, 22]]],
    [18, [[2, 17]]],
    [26, [[2, 24]]],
    [
      18,
      [
        [2, 15],
        [2, 16],
      ],
    ],
    [24, [[4, 19]]],
    [
      18,
      [
        [2, 14],
        [4, 15],
      ],
    ],
    [
      22,
      [
        [4, 18],
        [2, 19],
      ],
    ],
    [
      20,
      [
        [4, 16],
        [4, 17],
      ],
    ],
    [
      24,
      [
        [6, 19],
        [2, 20],
      ],
    ],
  ],
  H: [
    [17, [[1, 9]]],
    [28, [[1, 16]]],
    [22, [[2, 13]]],
    [16, [[4, 9]]],
    [
      22,
      [
        [2, 11],
        [2, 12],
      ],
    ],
    [28, [[4, 15]]],
    [
      26,
      [
        [4, 13],
        [1, 14],
      ],
    ],
    [
      26,
      [
        [4, 14],
        [2, 15],
      ],
    ],
    [
      24,
      [
        [4, 12],
        [4, 13],
      ],
    ],
    [
      28,
      [
        [6, 15],
        [2, 16],
      ],
    ],
  ],
};

const ALIGN: Record<number, number[]> = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
  7: [6, 22, 38],
  8: [6, 24, 42],
  9: [6, 26, 46],
  10: [6, 28, 50],
};

const ECL_BITS: Record<ErrorCorrectionLevel, number> = { L: 1, M: 0, Q: 3, H: 2 };

const ecEntry = (ecl: ErrorCorrectionLevel, version: number): EcEntry => EC[ecl][version - 1]!;
const totalData = (entry: EcEntry): number => entry[1].reduce((sum, b) => sum + b[0] * b[1], 0);

function pickVersion(byteLen: number, ecl: ErrorCorrectionLevel): number {
  for (let v = 1; v <= 10; v++) {
    const ccBits = v <= 9 ? 8 : 16;
    if (4 + ccBits + byteLen * 8 <= totalData(ecEntry(ecl, v)) * 8) return v;
  }
  throw new Error("QR content too large for versions 1–10");
}

// --- Bit/data assembly -----------------------------------------------------
function encodeCodewords(bytes: number[], version: number, ecl: ErrorCorrectionLevel): number[] {
  const entry = ecEntry(ecl, version);
  const cap = totalData(entry) * 8;
  const bits: number[] = [];
  const push = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1);
  };
  push(0b0100, 4); // byte mode
  push(bytes.length, version <= 9 ? 8 : 16);
  for (const b of bytes) push(b, 8);
  for (let i = 0; i < 4 && bits.length < cap; i++) bits.push(0); // terminator
  while (bits.length % 8 !== 0) bits.push(0);
  const pads = [0xec, 0x11];
  for (let pi = 0; bits.length < cap; pi++) push(pads[pi % 2]!, 8);

  const dataCw: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let v = 0;
    for (let j = 0; j < 8; j++) v = (v << 1) | bits[i + j]!;
    dataCw.push(v);
  }

  const ecLen = entry[0];
  const blocks: { data: number[]; ec: number[] }[] = [];
  let pos = 0;
  for (const [count, dcw] of entry[1]) {
    for (let b = 0; b < count; b++) {
      const data = dataCw.slice(pos, pos + dcw);
      pos += dcw;
      blocks.push({ data, ec: rsEncode(data, ecLen) });
    }
  }
  const maxData = Math.max(...blocks.map((b) => b.data.length));
  const out: number[] = [];
  for (let i = 0; i < maxData; i++) for (const b of blocks) if (i < b.data.length) out.push(b.data[i]!);
  for (let i = 0; i < ecLen; i++) for (const b of blocks) out.push(b.ec[i]!);
  return out;
}

// --- Matrix (flat arrays; -1 = unset) --------------------------------------
function maskFn(id: number, r: number, c: number): number {
  switch (id) {
    case 0:
      return (r + c) % 2 === 0 ? 1 : 0;
    case 1:
      return r % 2 === 0 ? 1 : 0;
    case 2:
      return c % 3 === 0 ? 1 : 0;
    case 3:
      return (r + c) % 3 === 0 ? 1 : 0;
    case 4:
      return (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0 ? 1 : 0;
    case 5:
      return ((r * c) % 2) + ((r * c) % 3) === 0 ? 1 : 0;
    case 6:
      return (((r * c) % 2) + ((r * c) % 3)) % 2 === 0 ? 1 : 0;
    default:
      return (((r + c) % 2) + ((r * c) % 3)) % 2 === 0 ? 1 : 0;
  }
}

function buildMatrix(version: number, ecl: ErrorCorrectionLevel, codewords: number[]): boolean[][] {
  const N = 17 + 4 * version;
  const m: number[] = new Array(N * N).fill(-1);
  const fn: number[] = new Array(N * N).fill(0);
  const ix = (r: number, c: number) => r * N + c;
  const set = (r: number, c: number, v: number) => {
    m[ix(r, c)] = v;
    fn[ix(r, c)] = 1;
  };
  const getM = (r: number, c: number) => m[ix(r, c)]!;
  const getF = (r: number, c: number) => fn[ix(r, c)]!;

  const finder = (br: number, bc: number) => {
    for (let dr = -1; dr <= 7; dr++)
      for (let dc = -1; dc <= 7; dc++) {
        const rr = br + dr,
          cc = bc + dc;
        if (rr < 0 || rr >= N || cc < 0 || cc >= N) continue;
        const ring =
          (dr >= 0 && dr <= 6 && (dc === 0 || dc === 6)) || (dc >= 0 && dc <= 6 && (dr === 0 || dr === 6));
        const core = dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4;
        set(rr, cc, ring || core ? 1 : 0);
      }
  };
  finder(0, 0);
  finder(0, N - 7);
  finder(N - 7, 0);

  for (let i = 8; i < N - 8; i++) {
    set(6, i, i % 2 === 0 ? 1 : 0);
    set(i, 6, i % 2 === 0 ? 1 : 0);
  }
  set(N - 8, 8, 1); // dark module

  const ap = ALIGN[version] ?? [];
  for (const r of ap)
    for (const c of ap) {
      if ((r <= 7 && c <= 7) || (r <= 7 && c >= N - 8) || (r >= N - 8 && c <= 7)) continue;
      for (let dr = -2; dr <= 2; dr++)
        for (let dc = -2; dc <= 2; dc++)
          set(r + dr, c + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1 ? 1 : 0);
    }

  // Reserve format + version info areas
  for (let i = 0; i <= 8; i++) {
    if (i !== 6) {
      fn[ix(8, i)] = 1;
      fn[ix(i, 8)] = 1;
    }
  }
  for (let i = 0; i < 8; i++) fn[ix(8, N - 1 - i)] = 1;
  for (let i = 0; i < 7; i++) fn[ix(N - 1 - i, 8)] = 1;
  if (version >= 7) {
    for (let i = 0; i < 6; i++)
      for (let j = 0; j < 3; j++) {
        fn[ix(i, N - 11 + j)] = 1;
        fn[ix(N - 11 + j, i)] = 1;
      }
  }

  // Data placement (zigzag from bottom-right)
  const dataBits: number[] = [];
  for (const cw of codewords) for (let b = 7; b >= 0; b--) dataBits.push((cw >> b) & 1);
  let bitIdx = 0;
  let upward = true;
  for (let col = N - 1; col > 0; col -= 2) {
    if (col === 6) col = 5;
    for (let i = 0; i < N; i++) {
      const row = upward ? N - 1 - i : i;
      for (let c = 0; c < 2; c++) {
        const cc = col - c;
        if (getF(row, cc) === 0) {
          m[ix(row, cc)] = bitIdx < dataBits.length ? dataBits[bitIdx++]! : 0;
        }
      }
    }
    upward = !upward;
  }

  // Try all masks, pick lowest penalty
  let best: number[] | null = null;
  let bestPenalty = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const cand = new Array<number>(N * N);
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) cand[ix(r, c)] = getF(r, c) ? getM(r, c) : getM(r, c) ^ maskFn(mask, r, c);
    placeFormat(cand, ix, ecl, mask, N);
    const p = penalty(cand, ix, N);
    if (p < bestPenalty) {
      bestPenalty = p;
      best = cand;
    }
  }

  const grid = best!;
  const out: boolean[][] = [];
  for (let r = 0; r < N; r++) {
    const row: boolean[] = [];
    for (let c = 0; c < N; c++) row.push(grid[ix(r, c)] === 1);
    out.push(row);
  }
  return out;
}

function placeFormat(
  cand: number[],
  ix: (r: number, c: number) => number,
  ecl: ErrorCorrectionLevel,
  mask: number,
  N: number,
) {
  const data = (ECL_BITS[ecl] << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >> 9) & 1 ? 0x537 : 0);
  const bits = ((data << 10) | (rem & 0x3ff)) ^ 0x5412;
  const get = (i: number) => (bits >> i) & 1;
  for (let i = 0; i <= 5; i++) cand[ix(8, i)] = get(i);
  cand[ix(8, 7)] = get(6);
  cand[ix(8, 8)] = get(7);
  cand[ix(7, 8)] = get(8);
  for (let i = 9; i <= 14; i++) cand[ix(14 - i, 8)] = get(i);
  for (let i = 0; i <= 7; i++) cand[ix(N - 1 - i, 8)] = get(i);
  for (let i = 8; i <= 14; i++) cand[ix(8, N - 15 + i)] = get(i);
}

function penalty(cand: number[], ix: (r: number, c: number) => number, N: number): number {
  const at = (r: number, c: number) => cand[ix(r, c)]!;
  let score = 0;
  for (let r = 0; r < N; r++) {
    let runC = 1,
      runR = 1;
    for (let c = 1; c < N; c++) {
      if (at(r, c) === at(r, c - 1)) {
        runC++;
        if (runC === 5) score += 3;
        else if (runC > 5) score++;
      } else runC = 1;
      if (at(c, r) === at(c - 1, r)) {
        runR++;
        if (runR === 5) score += 3;
        else if (runR > 5) score++;
      } else runR = 1;
    }
  }
  for (let r = 0; r < N - 1; r++)
    for (let c = 0; c < N - 1; c++) {
      const v = at(r, c);
      if (v === at(r, c + 1) && v === at(r + 1, c) && v === at(r + 1, c + 1)) score += 3;
    }
  let dark = 0;
  for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (at(r, c)) dark++;
  score += Math.floor(Math.abs((dark * 100) / (N * N) - 50) / 5) * 10;
  return score;
}

function toBytes(text: string): number[] {
  const out: number[] = [];
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    else
      out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3f), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
  }
  return out;
}

/** Generate a QR matrix (true = dark). Throws if content exceeds version 10. */
export function generateQR(text: string, ecl: ErrorCorrectionLevel = "M"): boolean[][] {
  const bytes = toBytes(text);
  const version = pickVersion(bytes.length, ecl);
  const codewords = encodeCodewords(bytes, version, ecl);
  return buildMatrix(version, ecl, codewords);
}
